import 'first-input-delay';
import { onINP, onTTFB, onCLS, onLCP, onFCP } from 'web-vitals';
import DataLayerService from '../../DataLayerService.js';

/**
 * Web Vitals Tracking
 *
 * Sammelt alle Web Vital Metriken und pusht sie als ein Event beim Seitenverlassen.
 * Folgt den Google Web Vitals Best Practices:
 * https://www.npmjs.com/package/web-vitals
 *
 * Metriken werden asynchron gesammelt:
 * - FCP, TTFB: Meist früh (vor/um window.load)
 * - LCP: Kurz nach window.load
 * - CLS, INP: Oft spät (nach User-Interaktion)
 *
 * Das finale Event wird gepusht bei:
 * - Page Visibility Change (Seite wird versteckt/verlassen)
 * - pagehide (Backup, bfcache-kompatibel — anders als beforeunload)
 */

const dataLayerService = new DataLayerService();

// Sammelt alle Web Vitals Metriken
const webVitalsData = {};
let eventPushed = false;

/**
 * Speichert eine Web Vital Metrik
 *
 * @param {Object} metric - Web Vitals Metric Object
 * @param {string} metric.name - Metrik-Name (CLS, INP, LCP, FCP, TTFB)
 * @param {number} metric.value - Aktueller Wert der Metrik
 * @param {number} metric.delta - Delta zum vorherigen Wert
 * @param {string} metric.rating - Bewertung (good, needs-improvement, poor)
 * @param {string} metric.id - Unique ID für diese Metrik-Messung
 */
function collectMetric(metric) {
	try {
		if (!metric || typeof metric !== 'object') {
			return;
		}

		const { name, delta, value, id, rating } = metric;

		if (!name) {
			return;
		}

		webVitalsData[name] = {
			value: delta, // Use delta so the value can be summed
			metric_id: id,
			metric_value: value,
			metric_delta: delta,
			metric_rating: rating
		};
	} catch (error) {
		console.error('[web-vitals] Failed to collect metric:', error);
	}
}

/**
 * Pusht alle gesammelten Web Vitals als ein Event
 */
function flushWebVitals() {
	if (eventPushed || Object.keys(webVitalsData).length === 0) {
		return;
	}

	try {
		const params = {};

		// Alle gesammelten Metriken als Parameter hinzufügen
		Object.entries(webVitalsData).forEach(([metricName, metric]) => {
			const prefix = metricName.toLowerCase();
			params[`${prefix}_value`] = metric.value;
			params[`${prefix}_metric_id`] = metric.metric_id;
			params[`${prefix}_metric_value`] = metric.metric_value;
			params[`${prefix}_metric_delta`] = metric.metric_delta;
			params[`${prefix}_metric_rating`] = metric.metric_rating;
		});

		dataLayerService.pushEvent('web_vitals', params);
		eventPushed = true;
	} catch (error) {
		console.error('[web-vitals] Failed to push web vitals event:', error);
	}
}

// Registriere Callbacks für alle Core Web Vitals
try {
	onCLS(collectMetric);
	onINP(collectMetric);
	onLCP(collectMetric);
	onFCP(collectMetric);
	onTTFB(collectMetric);
} catch (error) {
	console.error('[web-vitals] Failed to initialize web vitals tracking:', error);
}

// Push beim Seitenverlassen (Page Visibility API)
if (typeof document !== 'undefined') {
	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') {
			flushWebVitals();
		}
	});
}

// Backup: pagehide (bfcache-kompatibel, anders als beforeunload)
if (typeof window !== 'undefined') {
	window.addEventListener('pagehide', flushWebVitals);
}

// Export für Testing
export function __resetForTesting() {
	Object.keys(webVitalsData).forEach(key => delete webVitalsData[key]);
	eventPushed = false;
}
